/**
 * Message List Component
 * @implements FS-002.7.10 - Conversation Message Pagination
 * @implements US-020 - AI PM Conversation UI
 *
 * Displays a scrollable list of conversation messages with:
 * - Infinite scroll for loading older messages
 * - Scroll anchored to bottom (newest visible)
 * - Auto-scroll on new messages when near bottom
 */

import {
  useRef,
  useEffect,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { MessageBubble, type MessageBubbleProps } from './MessageBubble';
import { TypingIndicator } from './TypingIndicator';

/**
 * Message type for the list
 */
export interface Message extends Omit<MessageBubbleProps, 'key'> {
  id: string;
}

/**
 * Props for MessageList component
 */
export interface MessageListProps {
  /** Array of messages to display */
  messages: Message[];
  /** Whether older messages are being loaded */
  isLoadingMore?: boolean;
  /** Whether there are more messages to load */
  hasMore?: boolean;
  /** Callback to load more messages (triggered when scrolling to top) */
  onLoadMore?: () => void;
  /** Whether the AI PM is currently typing */
  isTyping?: boolean;
  /** Height of the message list container */
  height?: string | number;
}

/**
 * Ref handle for MessageList
 */
export interface MessageListRef {
  /** Scroll to the bottom of the list */
  scrollToBottom: (smooth?: boolean) => void;
}

/**
 * Distance from bottom to trigger auto-scroll on new messages (in pixels)
 */
const AUTO_SCROLL_THRESHOLD = 100;

/**
 * Distance from top to trigger load more (in pixels)
 */
const LOAD_MORE_THRESHOLD = 50;

/**
 * MessageList Component
 * @implements FS-002.7.10 - Message pagination and scroll behavior
 *
 * Displays messages in chronological order with:
 * - Initial load anchored to bottom
 * - Infinite scroll to load older messages
 * - Auto-scroll when new messages arrive and user is near bottom
 *
 * @example
 * ```tsx
 * <MessageList
 *   messages={messages}
 *   isLoadingMore={loading}
 *   hasMore={hasMoreMessages}
 *   onLoadMore={loadOlderMessages}
 *   isTyping={isWaitingForAI}
 * />
 * ```
 */
export const MessageList = forwardRef<MessageListRef, MessageListProps>(
  (
    {
      messages,
      isLoadingMore = false,
      hasMore = false,
      onLoadMore,
      isTyping = false,
      height = '100%',
    },
    ref,
  ) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const isNearBottomRef = useRef(true);
    const prevMessagesLengthRef = useRef(messages.length);

    /**
     * Scroll to the bottom of the list
     */
    const scrollToBottom = useCallback((smooth = true) => {
      if (containerRef.current) {
        containerRef.current.scrollTo({
          top: containerRef.current.scrollHeight,
          behavior: smooth ? 'smooth' : 'auto',
        });
      }
    }, []);

    /**
     * Expose scrollToBottom via ref
     */
    useImperativeHandle(
      ref,
      () => ({
        scrollToBottom,
      }),
      [scrollToBottom],
    );

    /**
     * Check if user is near the bottom of the list
     */
    const checkIfNearBottom = useCallback(() => {
      if (containerRef.current) {
        const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
        const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
        isNearBottomRef.current = distanceFromBottom < AUTO_SCROLL_THRESHOLD;
      }
    }, []);

    /**
     * Handle scroll events for infinite scroll and tracking position
     * @implements FS-002.7.10 - Load more when scrolling to top
     */
    const handleScroll = useCallback(() => {
      checkIfNearBottom();

      if (containerRef.current) {
        const { scrollTop } = containerRef.current;

        // Load more when near the top
        if (scrollTop < LOAD_MORE_THRESHOLD && hasMore && !isLoadingMore) {
          onLoadMore?.();
        }
      }
    }, [hasMore, isLoadingMore, onLoadMore, checkIfNearBottom]);

    /**
     * Scroll to bottom on initial mount
     * @implements FS-002.7.10 - Anchored to bottom (newest visible by default)
     */
    useEffect(() => {
      scrollToBottom(false);
    }, [scrollToBottom]);

    /**
     * Auto-scroll when new messages arrive and user is near bottom
     * @implements FS-002.7.10 - New messages auto-scroll if user is near bottom
     */
    useEffect(() => {
      if (messages.length > prevMessagesLengthRef.current) {
        if (isNearBottomRef.current) {
          scrollToBottom(true);
        }
      }
      prevMessagesLengthRef.current = messages.length;
    }, [messages.length, scrollToBottom]);

    /**
     * Auto-scroll when typing indicator appears and user is near bottom
     */
    useEffect(() => {
      if (isTyping && isNearBottomRef.current) {
        scrollToBottom(true);
      }
    }, [isTyping, scrollToBottom]);

    return (
      <Box
        ref={containerRef}
        onScroll={handleScroll}
        sx={{
          height,
          overflowY: 'auto',
          p: 2,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Load More Indicator */}
        {isLoadingMore && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              py: 2,
            }}
          >
            <CircularProgress size={24} />
          </Box>
        )}

        {/* Has More Hint */}
        {hasMore && !isLoadingMore && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ textAlign: 'center', py: 1 }}
          >
            Scroll up to load older messages
          </Typography>
        )}

        {/* Empty State */}
        {messages.length === 0 && !isLoadingMore && (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography variant="body2" color="text.secondary">
              No messages yet. Start the conversation!
            </Typography>
          </Box>
        )}

        {/* Messages */}
        {messages.map((message) => (
          <MessageBubble key={message.id} {...message} />
        ))}

        {/* Typing Indicator */}
        <TypingIndicator isVisible={isTyping} />
      </Box>
    );
  },
);

MessageList.displayName = 'MessageList';
